// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
// data-fit LLC · DecisionFlow · TenantAdmin Stage
// UserManager.jsx · DDL: R13 · API: v1.22 · Session 159
// Auth: useAuth() via AuthContext — ARCH-2 compliant
// Key constraints: self-modification blocked; tenant_id/server fields never sent; deactivate is soft, not hard delete
// Stage 12 endpoints wired — api_contract v1.15 §33
// OI-TSA-SHELL-CSS: Full Tailwind rewrite — BEM classes removed, shared primitives adopted

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Ban, Pencil, Plus, UserMinus, X } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { apiGet, apiPost, apiDelete } from '../../api/client.js'
import Button from '../shared/Button.jsx'
import Card from '../shared/Card.jsx'
import Pill from '../shared/Pill.jsx'
import { SCREEN_ORDER } from './TenantAdminShell'

const USER_TYPE_OPTIONS = [
  'tenant_super_admin',
  'comp_admin',
  'finance',
  'manager',
  'rep',
  'tool_admin',
]

const USER_TYPE_LABELS = {
  tenant_super_admin: 'Tenant Super Admin',
  comp_admin:         'Comp Admin',
  finance:            'Finance',
  manager:            'Manager',
  rep:                'Rep',
  tool_admin:         'Tool Admin',
  platform_admin:     'Platform Admin',
}

const ACTIVE_FILTER_OPTIONS = {
  ALL:      '',
  ACTIVE:   'true',
  INACTIVE: 'false',
}

const SORT_BY_OPTIONS = [
  { value: 'created_at',    label: 'Created' },
  { value: 'email',         label: 'Email' },
  { value: 'full_name',     label: 'Full Name' },
  { value: 'last_login_at', label: 'Last Login' },
]

const ERROR_MESSAGES = {
  SEAT_LIMIT_EXCEEDED:
    'Seat limit exceeded. Update tenant limits or deactivate another user before inviting this user.',
  SELF_MODIFICATION_BLOCKED:
    'Self-modification blocked. Tenant super admins cannot modify or deactivate their own user account here.',
  AUTH_INSUFFICIENT_ROLE:
    'You do not have sufficient permissions to manage tenant users.',
  NOT_FOUND: 'User not found.',
  GENERIC:   'Unable to complete the user management request.',
}

const EMPTY_INVITE_FORM = { email: '', full_name: '', user_type: 'comp_admin' }
const EMPTY_FILTERS = {
  user_type: '',
  is_active: ACTIVE_FILTER_OPTIONS.ALL,
  search:    '',
  sort_by:   'created_at',
  sort_dir:  'asc',
}
const EMPTY_EDIT_FORM   = { user_id: '', full_name: '', is_active: true }

function parseApiError(error) {
  if (error && typeof error === 'object') {
    const { code, message } = error
    return { code: code || 'UNKNOWN_ERROR', message: message || ERROR_MESSAGES.GENERIC }
  }
  return { code: 'UNKNOWN_ERROR', message: ERROR_MESSAGES.GENERIC }
}

function buildUserQuery({ cursor, filters }) {
  const params = new URLSearchParams()
  params.set('limit', '50')
  if (cursor) params.set('cursor', cursor)
  if (filters?.user_type) params.set('user_type', filters.user_type)
  if (filters?.is_active !== undefined && filters?.is_active !== null && filters.is_active !== '') {
    params.set('is_active', filters.is_active)
  }
  if (filters?.search?.trim()) params.set('search', filters.search.trim())
  if (filters?.sort_by)  params.set('sort_by',  filters.sort_by)
  if (filters?.sort_dir) params.set('sort_dir', filters.sort_dir)
  return params.toString()
}

function userTypeLabel(userType) {
  return USER_TYPE_LABELS[userType] || userType || 'Unknown'
}

// ---------------------------------------------------------------------------
// Shared input classes
// ---------------------------------------------------------------------------

const INPUT_CLS = `w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm placeholder-[rgb(var(--color-text-muted))] focus:outline-none disabled:opacity-50`

const SELECT_CLS = `w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none disabled:opacity-50`

const LABEL_CLS = `block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1`

// ---------------------------------------------------------------------------
// SortableHeader — clickable table column header with sort indicator
// Fires applyFilters after updating sort state so the request fires immediately.
// ---------------------------------------------------------------------------

function SortableHeader({ label, field, appliedFilters, onSort }) {
  const isActive = appliedFilters.sort_by === field
  const dir = appliedFilters.sort_dir
  return (
    <th
      className="pb-2 pr-4 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] cursor-pointer select-none hover:text-[rgb(var(--color-text))] "
      onClick={() => onSort(field)}
      title={`Sort by ${label}`}
    >
      {label}
      {isActive && <span className="ml-1 opacity-60">{dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  )
}

// ---------------------------------------------------------------------------
// SearchWithPicker — live debounced search + dropdown for UserManager filter
// On type: debounce 300ms → fires onSearch(value) automatically
// On select from dropdown: sets value to name and fires onSearch immediately
// ---------------------------------------------------------------------------

function SearchWithPicker({ value, onChange, onSearch, disabled }) {
  const [results, setResults]     = useState([])
  const [open, setOpen]           = useState(false)
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef(null)
  const wrapperRef  = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function handleInput(e) {
    const q = e.target.value
    onChange(q)
    clearTimeout(debounceRef.current)
    if (!q.trim()) {
      setResults([])
      setOpen(false)
      debounceRef.current = setTimeout(() => onSearch(''), 300)
      return
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const payload = await apiGet(`/api/v1/users?search=${encodeURIComponent(q.trim())}&sort_by=full_name&sort_dir=asc&limit=10`)
        const users = Array.isArray(payload?.data) ? payload.data : []
        setResults(users)
        setOpen(users.length > 0)
      } catch { setResults([]) } finally { setSearching(false) }
      onSearch(q)
    }, 300)
  }

  function handleSelect(u) {
    const label = u.full_name || u.email || ''
    onChange(label)
    setResults([])
    setOpen(false)
    onSearch(label)
  }

  function handleClear() {
    onChange('')
    setResults([])
    setOpen(false)
    onSearch('')
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex gap-1">
        <input
          type="text"
          className={INPUT_CLS}
          value={value}
          onChange={handleInput}
          placeholder="Name or email…"
          maxLength={200}
          disabled={disabled}
          autoComplete="off"
        />
        {value && (
          <button type="button" onClick={handleClear} disabled={disabled}
            className="shrink-0 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] ">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {searching && <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Searching…</p>}
      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-lg max-h-52 overflow-y-auto">
          {results.map((u) => (
            <li key={u.user_id}>
              <button type="button" onMouseDown={() => handleSelect(u)}
                className="w-full px-3 py-2 text-left text-tok-sm hover:bg-[rgb(var(--color-surface-muted))] ">
                <span className="font-medium text-[rgb(var(--color-text))] ">{u.full_name || '—'}</span>
                <span className="ml-2 text-[rgb(var(--color-text-muted))] ">{u.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// UserManager
// ---------------------------------------------------------------------------

export default function UserManager({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { user } = useAuth()

  const [users, setUsers]               = useState([])
  const [nextCursor, setNextCursor]     = useState(null)
  const [hasMore, setHasMore]           = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const [filters, setFilters]               = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)

  const [inviteForm, setInviteForm]   = useState(EMPTY_INVITE_FORM)
  const [editingUser, setEditingUser] = useState(null)
  const [editForm, setEditForm]       = useState(EMPTY_EDIT_FORM)
  const [confirmAction, setConfirmAction] = useState(null)

  const [error, setError]                   = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [loading, setLoading]               = useState(false)
  const [saving, setSaving]                 = useState(false)

  const isCurrentScreen = currentScreen === SCREEN_ORDER[0]

  const loadUsers = useCallback(
    async ({ cursor = null, filterValues = EMPTY_FILTERS, append = false } = {}) => {
      if (append) setIsLoadingMore(true)
      else setLoading(true)
      setError(null)

      try {
        const queryString = buildUserQuery({ cursor, filters: filterValues })
        // WIRED: GET /api/v1/users — v1.15 §33.1
        const payload = await apiGet(`/api/v1/users?${queryString}`)

        const data = Array.isArray(payload?.data) ? payload.data : []
        const pagination = payload?.pagination || {}
        setUsers((prev) => (append ? [...prev, ...data] : data))
        setNextCursor(pagination.next_cursor || null)
        setHasMore(Boolean(pagination.has_more))
      } catch (caughtError) {
        const { code, message } = parseApiError(caughtError)
        setError(code === 'AUTH_INSUFFICIENT_ROLE' ? ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE : message)
      } finally {
        setLoading(false)
        setIsLoadingMore(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (!isCurrentScreen) return
    loadUsers({ cursor: null, filterValues: appliedFilters, append: false })
  }, [appliedFilters, isCurrentScreen, loadUsers])

  function updateFilter(fieldName, value) {
    setFilters((current) => ({ ...current, [fieldName]: value }))
  }

  function applyFilters() {
    setSuccessMessage(null)
    setNextCursor(null)
    setUsers([])
    setAppliedFilters(filters)
  }

  function clearFilters() {
    setSuccessMessage(null)
    setFilters(EMPTY_FILTERS)
    setNextCursor(null)
    setUsers([])
    setAppliedFilters(EMPTY_FILTERS)
  }

  // Clicking a sortable column header toggles direction if already active,
  // else switches to that column asc. Applies immediately (no need to click Apply).
  function handleSort(field) {
    const newDir = appliedFilters.sort_by === field && appliedFilters.sort_dir === 'asc' ? 'desc' : 'asc'
    const newFilters = { ...appliedFilters, sort_by: field, sort_dir: newDir }
    setFilters(newFilters)
    setNextCursor(null)
    setUsers([])
    setAppliedFilters(newFilters)
  }

  function updateInviteForm(fieldName, value) {
    setInviteForm((current) => ({ ...current, [fieldName]: value }))
  }

  async function inviteUser() {
    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const body = {
        email:     inviteForm.email.trim(),
        full_name: inviteForm.full_name.trim(),
        user_type: inviteForm.user_type,
      }
      // WIRED: POST /api/v1/users/invite — v1.15 §33.2
      await apiPost('/api/v1/users', body)

      setInviteForm(EMPTY_INVITE_FORM)
      setSuccessMessage('Invitation sent successfully.')
      setNextCursor(null)
      setUsers([])
      await loadUsers({ cursor: null, filterValues: appliedFilters, append: false })
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SEAT_LIMIT_EXCEEDED') setError(ERROR_MESSAGES.SEAT_LIMIT_EXCEEDED)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  function beginEdit(targetUser) {
    setError(null)
    setSuccessMessage(null)
    setEditingUser(targetUser)
    setEditForm({
      user_id:   targetUser.user_id,
      full_name: targetUser.full_name || '',
      is_active: Boolean(targetUser.is_active),
    })
  }

  function cancelEdit() {
    setEditingUser(null)
    setEditForm(EMPTY_EDIT_FORM)
  }

  function updateEditForm(fieldName, value) {
    setEditForm((current) => ({ ...current, [fieldName]: value }))
  }

  function isSelfTarget(targetUserId) {
    return Boolean(targetUserId && user?.sub && targetUserId === user.sub)
  }

  async function patchUser(targetUser, patchValues) {
    if (!targetUser?.user_id) return
    if (isSelfTarget(targetUser.user_id)) { setError(ERROR_MESSAGES.SELF_MODIFICATION_BLOCKED); return }

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const body = {}
      if (Object.prototype.hasOwnProperty.call(patchValues, 'full_name')) body.full_name = patchValues.full_name
      if (Object.prototype.hasOwnProperty.call(patchValues, 'is_active'))  body.is_active  = patchValues.is_active

      // WIRED: PATCH /api/v1/users/{user_id} — v1.15 §33.3
      await fetch(`/api/v1/users/${targetUser.user_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      setUsers((currentUsers) =>
        currentUsers.map((u) => (u.user_id === targetUser.user_id ? { ...u, ...body } : u)),
      )
      setEditingUser(null)
      setEditForm(EMPTY_EDIT_FORM)
      setConfirmAction(null)
      setSuccessMessage('User updated successfully.')
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SELF_MODIFICATION_BLOCKED') setError(ERROR_MESSAGES.SELF_MODIFICATION_BLOCKED)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else if (code === '404' || code === 'USER_NOT_FOUND') setError(ERROR_MESSAGES.NOT_FOUND)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  async function deactivateUser(targetUser) {
    if (!targetUser?.user_id) return
    if (isSelfTarget(targetUser.user_id)) { setError(ERROR_MESSAGES.SELF_MODIFICATION_BLOCKED); setConfirmAction(null); return }

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // WIRED: DELETE /api/v1/users/{user_id} — v1.15 §33.4
      await apiDelete(`/api/v1/users/${targetUser.user_id}`)

      setUsers((currentUsers) =>
        currentUsers.map((u) =>
          u.user_id === targetUser.user_id ? { ...u, is_active: false } : u,
        ),
      )
      setConfirmAction(null)
      setEditingUser(null)
      setSuccessMessage('User deactivated successfully.')
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SELF_MODIFICATION_BLOCKED') setError(ERROR_MESSAGES.SELF_MODIFICATION_BLOCKED)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else if (code === '404' || code === 'USER_NOT_FOUND') setError(ERROR_MESSAGES.NOT_FOUND)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  function submitEdit() {
    if (!editingUser) return
    const nextIsActive = Boolean(editForm.is_active)
    if (editingUser.is_active && !nextIsActive) {
      setConfirmAction({
        type: 'deactivate-patch',
        user: editingUser,
        message: 'Deactivate this user account? The server will set is_active = false; no user row will be removed.',
      })
      return
    }
    patchUser(editingUser, { full_name: editForm.full_name.trim(), is_active: nextIsActive })
  }

  function requestDeactivate(targetUser) {
    setError(null)
    setSuccessMessage(null)
    if (isSelfTarget(targetUser.user_id)) { setError(ERROR_MESSAGES.SELF_MODIFICATION_BLOCKED); return }
    setConfirmAction({
      type: 'deactivate-delete',
      user: targetUser,
      message: 'Deactivate this user account? This is a soft deactivation. The user row remains in the database.',
    })
  }

  function confirmPendingAction() {
    if (!confirmAction) return
    if (confirmAction.type === 'deactivate-delete') { deactivateUser(confirmAction.user); return }
    if (confirmAction.type === 'deactivate-patch') {
      patchUser(confirmAction.user, { full_name: editForm.full_name.trim(), is_active: false })
    }
  }

  const canSubmitInvite = useMemo(() => (
    inviteForm.email.trim().length > 0 &&
    inviteForm.full_name.trim().length > 0 &&
    USER_TYPE_OPTIONS.includes(inviteForm.user_type)
  ), [inviteForm])

  const canSubmitEdit = useMemo(() => (
    Boolean(editingUser?.user_id && editForm.full_name.trim().length > 0)
  ), [editingUser, editForm])

  return (
    <div className="space-y-4">
      {/* Screen header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] ">TA-01</p>
          <h2 className="mt-0.5 text-tok-lg font-bold text-[rgb(var(--color-text))] ">User Manager</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))] ">
            Invite tenant users, maintain active status, and deactivate accounts without hard deleting records.
          </p>
        </div>
        <Button variant="secondary" onClick={() => onNavigate(SCREEN_ORDER[1])}>
          Role Assignments →
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))] " role="alert">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}
      {successMessage && (
        <div className="flex items-start gap-2 rounded-xl bg-[rgb(var(--color-success))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))] " role="status">
          <span className="flex-1">{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmAction && (
        <div className="rounded-2xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-4 " role="alertdialog" aria-modal="false">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-warning))] ">Confirm Deactivation</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-warning))] ">{confirmAction.message}</p>
          <div className="mt-3 flex gap-2">
            <Button variant="danger" onClick={confirmPendingAction} disabled={saving}>
              <UserMinus className="h-4 w-4" />
              {saving ? 'Deactivating...' : 'Confirm Deactivate'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmAction(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card title="Filters">
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">Blank filters are omitted from the request query string.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className={LABEL_CLS}>Search</label>
            <SearchWithPicker
              value={filters.search}
              onChange={(val) => updateFilter('search', val)}
              onSearch={(val) => {
                const newFilters = { ...filters, search: val }
                setFilters(newFilters)
                setNextCursor(null)
                setUsers([])
                setAppliedFilters(newFilters)
              }}
              disabled={loading || saving}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>User type</label>
            <select className={SELECT_CLS} value={filters.user_type} onChange={(e) => updateFilter('user_type', e.target.value)}>
              <option value="">All user types</option>
              {USER_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{userTypeLabel(t)}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Active status</label>
            <select className={SELECT_CLS} value={filters.is_active} onChange={(e) => updateFilter('is_active', e.target.value)}>
              <option value={ACTIVE_FILTER_OPTIONS.ALL}>All statuses</option>
              <option value={ACTIVE_FILTER_OPTIONS.ACTIVE}>Active</option>
              <option value={ACTIVE_FILTER_OPTIONS.INACTIVE}>Inactive</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Sort by</label>
            <div className="flex gap-1.5">
              <select
                className={SELECT_CLS}
                value={filters.sort_by}
                onChange={(e) => updateFilter('sort_by', e.target.value)}
              >
                {SORT_BY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                type="button"
                onClick={() => updateFilter('sort_dir', filters.sort_dir === 'asc' ? 'desc' : 'asc')}
                className="shrink-0 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] "
                title={filters.sort_dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
              >
                {filters.sort_dir === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-end gap-2">
          <Button onClick={applyFilters} disabled={loading || saving}>Apply</Button>
          <Button variant="secondary" onClick={clearFilters} disabled={loading || saving}>Clear</Button>
        </div>
      </Card>

      {/* Invite user */}
      <Card
        title="Invite User"
        action={<Plus className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
      >
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">
          Invite creates a user account with email, full name, and user type. Server-set fields are resolved by the API.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={LABEL_CLS}>Email</label>
            <input
              type="email"
              className={INPUT_CLS}
              value={inviteForm.email}
              onChange={(e) => updateInviteForm('email', e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Full name</label>
            <input
              type="text"
              className={INPUT_CLS}
              value={inviteForm.full_name}
              onChange={(e) => updateInviteForm('full_name', e.target.value)}
              placeholder="Full name"
            />
          </div>
          <div>
            <label className={LABEL_CLS}>User type</label>
            <select className={SELECT_CLS} value={inviteForm.user_type} onChange={(e) => updateInviteForm('user_type', e.target.value)}>
              {USER_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{userTypeLabel(t)}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={inviteUser} disabled={!canSubmitInvite || saving}>
            <Plus className="h-4 w-4" />
            {saving ? 'Sending...' : 'Send Invite'}
          </Button>
        </div>
      </Card>

      {/* Edit user */}
      {editingUser && (
        <Card title="Edit User">
          <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">Only full_name and is_active are patchable from this screen.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL_CLS}>Full name</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={editForm.full_name}
                onChange={(e) => updateEditForm('full_name', e.target.value)}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Active status</label>
              <select
                className={SELECT_CLS}
                value={editForm.is_active ? 'true' : 'false'}
                onChange={(e) => updateEditForm('is_active', e.target.value === 'true')}
              >
                <option value="true">Active</option>
                <option value="false">Inactive / Deactivated</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={submitEdit} disabled={!canSubmitEdit || saving}>
              <Pencil className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save User'}
            </Button>
            <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Users table */}
      <Card title="Users">
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">Cursor-paginated tenant users. Deactivation is soft and preserves the row.</p>

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-10 rounded-xl " />
            ))}
          </div>
        )}

        {!loading && users.length === 0 && (
          <p className="py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))] ">No users found for the selected filters.</p>
        )}

        {!loading && users.length > 0 && (
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-tok-sm">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface))] z-10">
                <tr className="border-b border-[rgb(var(--color-border))] ">
                  <SortableHeader label="Full Name"   field="full_name"     appliedFilters={appliedFilters} onSort={handleSort} />
                  <SortableHeader label="Email"       field="email"         appliedFilters={appliedFilters} onSort={handleSort} />
                  <th className="pb-2 pr-4 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] ">User Type</th>
                  <th className="pb-2 pr-4 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] ">Status</th>
                  <SortableHeader label="Last Login"  field="last_login_at" appliedFilters={appliedFilters} onSort={handleSort} />
                  <th className="pb-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] ">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))] ">
                {users.map((tenantUser) => (
                  <tr
                    key={tenantUser.user_id}
                    className={`${!tenantUser.is_active ? 'opacity-50' : ''}`}
                  >
                    <td className="py-2.5 pr-4 font-medium text-[rgb(var(--color-text))] ">{tenantUser.full_name || '—'}</td>
                    <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] ">{tenantUser.email || '—'}</td>
                    <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] ">{userTypeLabel(tenantUser.user_type)}</td>
                    <td className="py-2.5 pr-4">
                      <Pill tone={tenantUser.is_active ? 'Complete' : 'Blocked'}>
                        {tenantUser.is_active ? 'Active' : 'Inactive'}
                      </Pill>
                    </td>
                    <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))] ">{tenantUser.last_login_at || 'Never'}</td>
                    <td className="py-2.5">
                      <div className="flex gap-1.5">
                        <Button variant="ghost" onClick={() => beginEdit(tenantUser)} disabled={saving}>
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => requestDeactivate(tenantUser)}
                          disabled={saving || !tenantUser.is_active}
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Deactivate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && (
          <div className="mt-4">
            <Button
              variant="secondary"
              onClick={() => loadUsers({ cursor: nextCursor, filterValues: appliedFilters, append: true })}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
